import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { mediaUrl } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export default function Header() {
  const { settings } = useSettings();
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const nav = settings.navigation?.main || [];
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const onSearch = async (val) => {
    setQ(val);
    if (val.length < 2) return setResults([]);
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(val)}`);
      setResults(data);
    } catch {
      setResults([]);
    }
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#F7F5F1]/90 border-b border-[#E8E1D8]" data-testid="site-header">
      <div className="onusa-container flex items-center justify-between h-16 md:h-20">
        <div className="flex items-center gap-3 md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button aria-label="Menu" data-testid="mobile-menu-trigger"><Menu size={22} /></button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#F7F5F1] w-[82%]">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-8 flex flex-col gap-6">
                {nav.map((n) => (
                  <Link key={n.href} to={n.href} className="heading-x text-lg" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</Link>
                ))}
                <div className="rule-x pt-6 flex flex-col gap-4">
                  {user && ["admin", "super_admin"].includes(user.role) && (
                    <Link to="/admin" className="label-x font-semibold" data-testid="mobile-admin-link">→ ADMIN DASHBOARD</Link>
                  )}
                  <Link to="/account" className="label-x">ACCOUNT</Link>
                  <Link to="/account/wishlist" className="label-x">WISHLIST</Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <Link key={n.href} to={n.href} className="font-mono text-[0.68rem] uppercase tracking-[0.22em] hover:opacity-60 transition-opacity" data-testid={`nav-${n.label.toLowerCase()}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <Link to="/" className="absolute left-1/2 -translate-x-1/2" data-testid="logo-link">
          {settings.logo_url ? (
            <img src={mediaUrl(settings.logo_url)} alt="ONUSA" className="h-6 object-contain" />
          ) : (
            <span className="font-display text-xl md:text-2xl tracking-[0.42em] font-semibold pl-3">ONUSA</span>
          )}
        </Link>

        <div className="flex items-center gap-4 md:gap-5">
          {user && ["admin", "super_admin"].includes(user.role) && (
            <Link to="/admin" className="hidden sm:inline-block font-mono text-[0.62rem] uppercase tracking-[0.2em] border border-[#1D1D1B] px-3 py-1.5 hover:bg-[#1D1D1B] hover:text-[#F7F5F1] transition-colors" data-testid="header-admin-link">ADMIN</Link>
          )}
          <button aria-label="Search" onClick={() => setSearchOpen((s) => !s)} data-testid="search-trigger"><Search size={19} /></button>
          <Link to="/account" aria-label="Account" className="hidden sm:block" data-testid="account-link">
            <User size={19} />
          </Link>
          <Link to="/account/wishlist" aria-label="Wishlist" className="hidden sm:block" data-testid="wishlist-link">
            <Heart size={19} />
          </Link>
          <button aria-label="Cart" className="relative" onClick={() => setOpen(true)} data-testid="cart-trigger">
            <ShoppingBag size={19} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#1D1D1B] text-[#F7F5F1] text-[0.6rem] font-mono h-4 min-w-4 px-1 flex items-center justify-center rounded-full" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-[#E8E1D8] bg-white" data-testid="search-panel">
          <div className="onusa-container py-4">
            <div className="flex items-center gap-3 border-b border-[#1D1D1B] pb-2">
              <Search size={18} />
              <input
                autoFocus
                value={q}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="ZOEK PRODUCTEN, SKU, COLLECTIES"
                className="flex-1 bg-transparent outline-none font-mono text-xs uppercase tracking-[0.15em]"
                data-testid="search-input"
              />
              <button onClick={() => { setSearchOpen(false); setQ(""); setResults([]); }}><X size={18} /></button>
            </div>
            {results.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-5">
                {results.map((r) => (
                  <button key={r.slug} onClick={() => { navigate(`/product/${r.slug}`); setSearchOpen(false); }} className="text-left group" data-testid={`search-result-${r.slug}`}>
                    <div className="aspect-[3/4] bg-[#E8E1D8] overflow-hidden">
                      <img src={mediaUrl(r.images?.[0])} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <p className="mt-2 text-xs uppercase tracking-wide">{r.name}</p>
                    <p className="label-x mt-1">€{(r.sale_price || r.price).toFixed(2)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
